// Keeping your stands, pins and catches safe:
//  - an automatic copy on the phone every day (the last 7 are kept), plus one before every import or restore,
//    so anything deleted by mistake or lost to a bad import can be brought back;
//  - when the last backup file was made, and a reminder when it's been a while.
// The copies leave out photos (those stay where they are).
import type { Snapshot } from "../shared/model.ts";
import { dbAll, dbDel, dbGet, dbPut } from "./db.ts";
import { $, $maybe, el, toast } from "./dom.ts";
import { dFmt, t, tFmt } from "./i18n.ts";
import { upgrade } from "./species.ts";
import { catches, onRender, render, setCatches } from "./state.ts";
import { dayKey, lsGet, lsSet } from "./util.ts";
import { loadPlaces, pins } from "./map/places.ts";

const KEEP_SNAPSHOTS = 7, REMIND_DAYS = 14;

/** Saves a copy of catches, pins and routes. Without a reason it's the day's automatic copy. */
export async function takeSnapshot(reason?: "import" | "restore"): Promise<Snapshot | null> {
  const [cs, ps, rs] = await Promise.all([dbAll("catches"), dbAll("pins"), dbAll("routes")]);
  if (!cs.length && !ps.length && !rs.length) return null;
  const now = Date.now();
  const snap: Snapshot = { id: reason ? `${dayKey(now)}-${reason}-${now}` : dayKey(now), at: now, reason: reason ?? "daily", catches: cs, pins: ps, routes: rs };
  await dbPut("snapshots", snap);
  const all = (await dbAll("snapshots")).sort((a, b) => b.at - a.at);
  for (const s of all.slice(KEEP_SNAPSHOTS)) await dbDel("snapshots", s.id);
  return snap;
}
async function dailySnapshot(): Promise<void> {
  try { if (!(await dbGet("snapshots", dayKey(Date.now())))) await takeSnapshot(); }
  catch (e) { console.warn("Daily copy failed", e); }
}
// Puts back everything from a copy that is missing now. Nothing newer is removed or overwritten.
async function restoreSnapshot(id: string): Promise<void> {
  const s = await dbGet("snapshots", id);
  if (!s) return;
  await takeSnapshot("restore");
  let n = 0;
  for (const x of s.catches ?? []) if (!(await dbGet("catches", x.id))) { await dbPut("catches", x); n++; }
  for (const x of s.pins ?? []) if (!(await dbGet("pins", x.id))) { await dbPut("pins", x); n++; }
  for (const x of s.routes ?? []) if (!(await dbGet("routes", x.id))) { await dbPut("routes", x); n++; }
  setCatches((await dbAll("catches")).map(upgrade));
  await loadPlaces();
  render();
  toast(n ? t("snapRestored", n) : t("snapNothing"));
}

/** Notes that a backup file was just made. */
export function markBackedUp(): void {
  lsSet("lastBackup", String(Date.now()));
  safetyRender();
}
function ago(ts: number): string {
  const days = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(ts).setHours(0, 0, 0, 0)) / 864e5);
  return days <= 0 ? t("agoToday") : days === 1 ? t("agoYesterday") : t("agoDays", days);
}

async function renderSafetyPanel(): Promise<void> {
  const info = $maybe("#backup-info"), list = $maybe("#snapshot-list");
  if (!info || !list) return;
  const last = Number(lsGet("lastBackup", "0"));
  let persisted: boolean | null = null;
  try { persisted = (await navigator.storage?.persisted?.()) ?? null; } catch { /* unknown */ }
  info.replaceChildren(
    el("span", { text: last ? t("backupLast", ago(last)) : t("backupNever") }),
    persisted === true ? el("span", { text: t("storageProtected") }) : persisted === false ? el("span", { class: "warnline", text: t("storageNotProtected") }) : "");
  const snaps = (await dbAll("snapshots").catch((): Snapshot[] => [])).sort((a, b) => b.at - a.at);
  if (!snaps.length) { list.replaceChildren(el("p", { class: "hint", text: t("snapNone") })); return; }
  list.replaceChildren(...snaps.map((s) => {
    const btn = el("button", { class: "btn small", type: "button" }, t("snapRestore"));
    let armed = false;
    btn.addEventListener("click", async () => {
      if (!armed) {
        armed = true; btn.classList.add("armed"); btn.textContent = t("snapConfirm");
        setTimeout(() => { armed = false; btn.classList.remove("armed"); btn.textContent = t("snapRestore"); }, 3500);
        return;
      }
      btn.disabled = true; await restoreSnapshot(s.id); btn.disabled = false;
    });
    return el("div", { class: "snaprow" },
      el("div", {}, el("b", { class: "num", text: `${dFmt.format(s.at)} ${tFmt.format(s.at)}` }),
        el("small", { text: t("snapCounts", s.pins.length, s.routes.length, s.catches.length) + (s.reason !== "daily" ? " · " + t(`snapReason_${s.reason}`) : "") })),
      btn);
  }));
}

// A gentle reminder on the Catches tab once there is something worth backing up.
function renderBackupNudge(): void {
  const box = $maybe("#backup-nudge");
  if (!box) return;
  const last = Number(lsGet("lastBackup", "0")), snoozed = Number(lsGet("backupSnooze", "0"));
  const items = catches.length + pins.length;
  const due = items >= 5 && Date.now() - last > REMIND_DAYS * 864e5 && Date.now() > snoozed;
  if (!due) { box.hidden = true; return; }
  box.replaceChildren(el("div", { class: "nudge" },
    el("div", {}, el("b", { text: t("nudgeTitle") }), el("p", { text: last ? t("nudgeText", ago(last), items) : t("nudgeTextNever", items) })),
    el("div", { class: "nudgebtns" },
      el("button", { class: "btn primary small", type: "button", onclick: () => $("#export").click() }, t("nudgeNow")),
      el("button", { class: "linkbtn", type: "button", onclick: () => { lsSet("backupSnooze", String(Date.now() + 3 * 864e5)); renderBackupNudge(); } }, t("nudgeLater")))));
  box.hidden = false;
}

function safetyRender(): void {
  renderBackupNudge();
  void renderSafetyPanel();
}
onRender(safetyRender);
export async function safetyStart(): Promise<void> {
  await dailySnapshot();
  navigator.storage?.persist?.().catch(() => {}); // ask the browser not to clear the data
  safetyRender();
}
